import { Link } from "react-router-dom";
import {
    ShieldCheck,
    Activity,
    Cable,
    Zap,
    Tv,
    Headset,
    ArrowRight,
} from "lucide-react";
import { PageHeader, FadeUp, Counter } from "../components/Reveal";

const FEATURES = [
    {
        icon: ShieldCheck,
        title: "15+ Years of Trust",
        text: "Trusted local connectivity for more than 15 years.",
    },
    {
        icon: Activity,
        title: "99.9% Uptime",
        text: "Reliable internet designed to keep you connected.",
    },
    {
        icon: Cable,
        title: "True FTTH",
        text: "Fiber-to-the-home connectivity.",
    },
    {
        icon: Zap,
        title: "High-Speed Internet",
        text: "Smooth browsing, streaming, gaming and everyday usage.",
    },
    {
        icon: Tv,
        title: "Entertainment",
        text: "TV channels and OTT options for the whole family.",
    },
    {
        icon: Headset,
        title: "Local Customer Support",
        text: "Accessible support from a trusted local provider.",
    },
];

const STATS = [
    { value: 15, suffix: "+", decimals: 0, label: "Years of Trust" },
    { value: 99.9, suffix: "%", decimals: 1, label: "Network Uptime" },
    { value: 240, suffix: "+", decimals: 0, label: "Live TV Channels" },
    { value: 14, suffix: "+", decimals: 0, label: "OTT Apps" },
];

export default function WhyEway() {
    return (
        <>
            <PageHeader
                testId="why-eway-header"
                eyebrow="WHY EWAY"
                title={
                    <>
                        Why Choose <span className="text-gradient-fire">Eway Fiber Net?</span>
                    </>
                }
                subtitle="Fast, reliable and local — connectivity built on 15+ years of trust in Narasaraopet."
            />

            <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
                <div
                    className="glass mb-14 grid grid-cols-2 gap-6 rounded-3xl p-8 sm:p-10 lg:grid-cols-4"
                    data-testid="why-eway-stats"
                >
                    {STATS.map((s) => (
                        <div key={s.label} className="text-center">
                            <p className="font-mono text-3xl font-black text-white sm:text-5xl">
                                <Counter to={s.value} suffix={s.suffix} decimals={s.decimals} className="text-gradient-fire" />
                            </p>
                            <p className="mt-2 text-xs font-semibold tracking-wide text-slate-400 sm:text-sm">
                                {s.label}
                            </p>
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {FEATURES.map((f, i) => (
                        <FadeUp key={f.title} delay={i * 0.07} className="h-full">
                            <div
                                data-testid={`why-card-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="glass group h-full rounded-3xl p-7 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1.5 hover:border-[#E61C24]/40 hover:shadow-[0_16px_48px_rgba(230,28,36,0.16)]"
                            >
                                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#E61C24]/15 text-[#ff5a60] transition-transform duration-300 group-hover:scale-110">
                                    <f.icon className="h-6 w-6" />
                                </div>
                                <h3 className="font-display mt-5 text-xl font-bold text-white">
                                    {f.title}
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                                    {f.text}
                                </p>
                            </div>
                        </FadeUp>
                    ))}
                </div>

                <FadeUp delay={0.1}>
                    <div className="mt-14 text-center">
                        <Link
                            to="/get-connected"
                            data-testid="why-eway-cta"
                            className="group inline-flex items-center gap-3 rounded-full bg-[#E61C24] px-8 py-4 text-base font-extrabold text-white shadow-[0_10px_44px_rgba(230,28,36,0.5)] transition-[transform,background-color] duration-200 hover:scale-105 hover:bg-[#C7151C]"
                        >
                            Get New Connection
                            <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
                        </Link>
                    </div>
                </FadeUp>
            </section>
        </>
    );
}
